import React from "react";
import { GsdAppShell } from "./GsdAppShell.jsx";
import { GsdSidebarNav } from "./GsdSidebarNav.jsx";
import { GsdModuleNavItem } from "./GsdModuleNavItem.jsx";
import { GsdRoleSwitcher } from "./GsdRoleSwitcher.jsx";
import { resolveRoleShellMode, shellModeToLayoutVariant } from "../config/resolveRoleShell.js";
import { resolveVisibleNavGroups } from "../config/resolveNavigation.js";

/**
 * Barre contexte utilisateur + switch rôle (admin multi-vue).
 */
export function GsdUserContextBar({ userDisplayName, roleLabel, roleIcon, switcher, testId = "gsd-user-context-bar" }) {
  return (
    <div className="gsd-user-context-bar" data-testid={testId}>
      {roleIcon ? <span aria-hidden="true">{roleIcon}</span> : null}
      <span className="gsd-user-context-bar__user">{userDisplayName}</span>
      {roleLabel ? <span className="gsd-user-context-bar__role">{roleLabel}</span> : null}
      {switcher}
    </div>
  );
}

/** Navigation publique horizontale simplifiée. */
export function GsdPublicNav(props) {
  return <GsdSidebarNav {...props} testId={props.testId || "gsd-public-nav"} className="gsd-public-nav" />;
}

/** Sidebar admin latérale lisible (PokerPUR ShellSidebar + GolfMAP layout-shell). */
export function GsdAdminSidebar({
  items = [],
  renderLink,
  isItemActive,
  title,
  subtitle,
  className = "",
  testId = "gsd-admin-sidebar",
}) {
  return (
    <aside className={`gsd-admin-sidebar gsd-role-sidebar${className ? ` ${className}` : ""}`} data-testid={testId}>
      <div className="gsd-role-sidebar__header">
        <p className="gsd-role-sidebar__title">{title || "Administration"}</p>
        {subtitle ? <p className="gsd-role-sidebar__subtitle">{subtitle}</p> : null}
      </div>
      <nav className="gsd-admin-sidebar__nav" aria-label="Administration">
        {items.map((item) => (
          <GsdModuleNavItem key={item.id} item={item} isActive={isItemActive?.(item)} renderLink={renderLink} className="gsd-admin-sidebar__item" />
        ))}
      </nav>
    </aside>
  );
}

/** Sidebar gestionnaire / Sensei. */
export function GsdManagerSidebar({ title, subtitle, ...props }) {
  return (
    <GsdAdminSidebar
      {...props}
      title={title || "Gestionnaire"}
      subtitle={subtitle}
      className="gsd-manager-sidebar"
      testId={props.testId || "gsd-manager-sidebar"}
    />
  );
}

/** Nav primaire mobile — PokerPUR ShellPrimaryNav. */
export function GsdPrimaryNav({ items = [], renderLink, isItemActive, testId = "gsd-primary-nav" }) {
  if (!items.length) return null;
  return (
    <nav className="gsd-primary-nav" aria-label="Navigation principale" data-testid={testId}>
      <div className="gsd-primary-nav__inner">
        {items.map((item) => (
          <GsdModuleNavItem key={item.id} item={item} isActive={isItemActive?.(item)} renderLink={renderLink} className="gsd-primary-nav__item" />
        ))}
      </div>
    </nav>
  );
}

/** Onglets joueur — dans chrome sticky sous header. */
export function GsdPlayerTabs({ items = [], renderLink, isItemActive, testId = "gsd-player-tabs" }) {
  if (!items.length) return null;
  return (
    <div className="gsd-player-chrome" data-testid="gsd-player-chrome">
      <nav className="gsd-player-tabs" aria-label="Mon espace" data-testid={testId}>
        {items.map((item) => (
          <GsdModuleNavItem key={item.id} item={item} isActive={isItemActive?.(item)} renderLink={renderLink} className="gsd-player-tabs__tab" />
        ))}
      </nav>
    </div>
  );
}

/** Menu mobile par rôle. */
export function GsdMobileRoleNav({ items = [], renderLink, isItemActive, testId = "gsd-mobile-role-nav" }) {
  return (
    <nav className="gsd-mobile-role-nav" data-testid={testId}>
      {items.map((item) => (
        <GsdModuleNavItem key={item.id} item={item} isActive={isItemActive?.(item)} renderLink={renderLink} className="gsd-mobile-role-nav__item" />
      ))}
    </nav>
  );
}

export function GsdTopActions({ children, testId = "gsd-top-actions" }) {
  return (
    <div className="gsd-top-actions" data-testid={testId}>
      {children}
    </div>
  );
}

/** Layout admin dashboard avec sidebar. */
export function GsdAdminDashboardLayout({ sidebar, children, testId = "gsd-admin-dashboard-layout" }) {
  return (
    <div className="gsd-admin-dashboard-layout" data-testid={testId}>
      {sidebar}
      <div className="gsd-admin-dashboard-layout__main">{children}</div>
    </div>
  );
}

/** Panneaux admin démo — comptes, rôles, demandes. */
export function GsdAccountsAdminPanel({ accounts = [], testId = "gsd-accounts-admin-panel" }) {
  return (
    <div className="gsd-admin-panel" data-testid={testId}>
      <h2>Comptes</h2>
      <table className="gsd-admin-panel__table">
        <thead>
          <tr>
            <th>Identifiant</th>
            <th>Nom</th>
            <th>Rôle</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.id || a.username} data-testid={`gsd-account-row-${a.username}`}>
              <td>{a.username}</td>
              <td>{a.name}</td>
              <td>{a.role}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GsdRolesAdminPanel({ roles = [], testId = "gsd-roles-admin-panel" }) {
  return (
    <div className="gsd-admin-panel" data-testid={testId}>
      <h2>Rôles</h2>
      <ul className="gsd-admin-panel__list">
        {roles.map((r) => (
          <li key={r.id}>
            {r.icon ? <span aria-hidden="true">{r.icon}</span> : null} {r.label || r.id}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GsdAccessRequestsPanel({ requests = [], testId = "gsd-access-requests-panel" }) {
  const statusLabel = (s) => s || "reçue";
  return (
    <div className="gsd-admin-panel" data-testid={testId}>
      <h2>Demandes de compte</h2>
      {requests.length === 0 ? (
        <p className="muted">Aucune demande [DÉMO].</p>
      ) : (
        <table className="gsd-admin-panel__table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Courriel</th>
              <th>Rôle</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} data-testid={`gsd-access-request-${r.id}`}>
                <td>{r.name}</td>
                <td>{r.email}</td>
                <td>{r.role}</td>
                <td>{statusLabel(r.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Shell connecté unifié — choisit admin sidebar / manager sidebar / player tabs / public top-nav.
 */
export function GsdConnectedShell({
  config,
  activeRole,
  isAuthenticated,
  pathname = "",
  sidebarItems = [],
  playerTabItems = [],
  roleSwitcherProps,
  userContextBar,
  shellMode: shellModeProp,
  navGroups: navGroupsProp,
  children,
  ...shellProps
}) {
  const shellMode = shellModeProp || resolveRoleShellMode(activeRole, { isAuthenticated, pathname });
  const layoutVariant = shellModeToLayoutVariant(shellMode);
  const hideHeaderNav =
    shellMode === "admin" || shellMode === "manager" || shellMode === "player" || shellMode === "auth";

  const navGroups =
    navGroupsProp ??
    (shellMode === "public"
      ? resolveVisibleNavGroups(config, { role: "guest", isAuthenticated: false, locale: shellProps.locale || "fr" }).filter((g) => g.id === "public")
      : []);

  const locale = shellProps.locale || "fr";
  const sidebarTitle =
    shellMode === "admin"
      ? locale === "en"
        ? "Administration"
        : "Administration"
      : shellMode === "manager"
        ? locale === "en"
          ? "Manager space"
          : "Espace Sensei"
        : undefined;
  const sidebarSubtitle =
    shellMode === "admin"
      ? locale === "en"
        ? "Platform management"
        : "Gestion plateforme"
      : shellMode === "manager"
        ? locale === "en"
          ? "My dojo"
          : "Mon dojo"
        : undefined;

  const sidebar =
    shellMode === "admin" ? (
      <GsdAdminSidebar
        items={sidebarItems}
        title={sidebarTitle}
        subtitle={sidebarSubtitle}
        renderLink={shellProps.renderNavLink}
        isItemActive={shellProps.isItemActive}
      />
    ) : shellMode === "manager" ? (
      <GsdManagerSidebar
        items={sidebarItems}
        title={sidebarTitle}
        subtitle={sidebarSubtitle}
        renderLink={shellProps.renderNavLink}
        isItemActive={shellProps.isItemActive}
      />
    ) : null;

  const primaryNav =
    shellMode === "admin" || shellMode === "manager" ? (
      <GsdPrimaryNav items={sidebarItems} renderLink={shellProps.renderNavLink} isItemActive={shellProps.isItemActive} />
    ) : null;

  const playerTabs =
    shellMode === "player" ? (
      <GsdPlayerTabs items={playerTabItems} renderLink={shellProps.renderNavLink} isItemActive={shellProps.isItemActive} />
    ) : null;

  const mobileNavItems = sidebarItems.length ? sidebarItems : playerTabItems;

  return (
    <GsdAppShell
      {...shellProps}
      config={config}
      activeRole={activeRole}
      isAuthenticated={isAuthenticated}
      layoutVariant={layoutVariant}
      navGroups={hideHeaderNav ? [] : navGroups}
      hideHeaderNav={hideHeaderNav}
      userContextSlot={userContextBar}
      roleSwitcherSlot={roleSwitcherProps ? <GsdRoleSwitcher {...roleSwitcherProps} locale={locale} /> : null}
      sidebarSlot={sidebar}
      primaryNavSlot={primaryNav}
      playerTabsSlot={playerTabs}
      mobileNavItems={mobileNavItems}
      shellMode={shellMode}
      testId={shellProps.testId || "gsd-connected-shell"}
    >
      {children}
    </GsdAppShell>
  );
}

export function GsdManagerShell(props) {
  return <GsdConnectedShell {...props} shellMode="manager" activeRole={props.activeRole || "sensei"} isAuthenticated testId="gsd-manager-shell" />;
}

export function GsdPlayerShell(props) {
  return <GsdConnectedShell {...props} shellMode="player" activeRole={props.activeRole || "student"} isAuthenticated testId="gsd-player-shell" />;
}

// GsdAdminShell enriched — force sidebar via ConnectedShell
export function GsdAdminConnectedShell(props) {
  return <GsdConnectedShell {...props} shellMode="admin" activeRole={props.activeRole || "hanshi"} isAuthenticated testId="gsd-admin-connected-shell" />;
}
